import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer, Icon, Empty, Button, Tabs, Badge, Tooltip } from 'antd';
import { useNotification } from '~/context/NotificationContext';
import { useHistory } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import moment from 'moment';

var TabPane = Tabs.TabPane;

/* ─── Styled Components ─── */

var NotificationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #f5f5f5;
  position: relative;
  background: ${function(props) { return props.isUnread ? '#f0f7ff' : '#fff'; }};
  border-left: 3px solid ${function(props) {
    switch (props.notifType) {
      case 'success': return '#52c41a';
      case 'error': return '#f5222d';
      case 'warning': return '#faad14';
      default: return '#1890ff';
    }
  }};

  &:hover {
    background: ${function(props) { return props.isUnread ? '#e6f0fa' : '#fafafa'; }};
  }

  &:last-child {
    border-bottom: none;
  }
`;

var NotificationIconCircle = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function(props) { return props.bg || '#e6f7ff'; }};
  border: 1.5px solid ${function(props) { return props.borderColor || '#91d5ff'; }};
`;

var NotificationBody = styled.div`
  flex: 1;
  min-width: 0;
`;

var NotificationMessage = styled.div`
  color: rgba(0, 0, 0, 0.85);
  font-size: 13px;
  line-height: 1.5;

  a { color: #1890ff; text-decoration: none; }
  a:hover { text-decoration: underline; }
  p { margin: 0; }
  ul, ol { margin: 4px 0; padding-left: 20px; }
`;

/* Miniatura do vídeo. Só aparece quando o aviso traz capa — é o que
   diferencia "leia isto" de "assista a isto" antes de a pessoa clicar. */
var VideoThumb = styled.div`
  position: relative;
  margin-top: 8px;
  border-radius: 6px;
  overflow: hidden;
  line-height: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);

  img {
    width: 100%;
    display: block;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }

  &:hover .play {
    transform: scale(1.08);
  }

  /* No canto, não no centro: a capa tem a marca no meio, e um disco em cima
     dela parte a palavra ao meio. */
  .play {
    position: absolute;
    left: 8px;
    bottom: 8px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(75, 33, 131, 0.92);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: transform 0.15s;
    /* O clique é da miniatura inteira; o disco não pode roubá-lo. */
    pointer-events: none;
  }
`;

var NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

var NotificationTime = styled.span`
  color: rgba(0, 0, 0, 0.35);
  font-size: 11px;
`;

var UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1890ff;
  flex-shrink: 0;
  display: inline-block;
`;

var RowActions = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
  background: inherit;
  padding-left: 8px;

  ${NotificationRow}:hover & {
    opacity: 1;
  }
`;

var ActionBtn = styled(Button)`
  color: #bfbfbf !important;
  &:hover { color: #595959 !important; }
`;

var EmptyContainer = styled.div`
  padding: 64px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

var EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

var EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 6px;
`;

var EmptyDesc = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  max-width: 240px;
`;

var pulseAnimation = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.6); }
  70%  { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
`;

var LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #52c41a;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  flex-shrink: 0;
`;

var TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

var DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 24px;
`;

var DrawerTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
`;

var StyledTabs = styled(Tabs)`
  .ant-tabs-bar {
    margin: 0;
    padding: 0;
  }
`;

var GroupHeader = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 1;
`;

var LoadMoreBtn = styled.div`
  padding: 14px 16px;
  text-align: center;
  cursor: pointer;
  color: #1890ff;
  font-size: 13px;
  font-weight: 500;
  border-top: 1px solid #f0f0f0;
  transition: background 0.15s;

  &:hover {
    background: #f5f5f5;
  }
`;

/* ─── Constants ─── */

var ICON_CONFIG = {
  success: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: 'check-circle' },
  error: { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', icon: 'close-circle' },
  warning: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', icon: 'warning' },
  info: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', icon: 'info-circle' },
};

var PAGE_SIZE = 20;

/* ─── Helpers ─── */

var groupByTime = function(items) {
  var today = moment().startOf('day');
  var yesterday = moment().subtract(1, 'day').startOf('day');
  var weekAgo = moment().subtract(7, 'days').startOf('day');

  var groups = {
    'Hoje': [],
    'Ontem': [],
    'Esta semana': [],
    'Anteriores': [],
  };

  items.forEach(function(item) {
    var created = moment(item.created_at);
    if (created.isSameOrAfter(today)) {
      groups['Hoje'].push(item);
    } else if (created.isSameOrAfter(yesterday)) {
      groups['Ontem'].push(item);
    } else if (created.isSameOrAfter(weekAgo)) {
      groups['Esta semana'].push(item);
    } else {
      groups['Anteriores'].push(item);
    }
  });

  return groups;
};

/* ─── Component ─── */

var NotificationDrawer = function(props) {
  var visible = props.visible;
  var onClose = props.onClose;

  var context = useNotification();
  var notifications = context.notifications;
  var realtimeConnected = context.realtimeConnected;
  var markAsRead = context.markAsRead;
  var markAllAsRead = context.markAllAsRead;
  var deleteNotification = context.deleteNotification;

  var history = useHistory();

  var activeTabState = useState('all');
  var activeTab = activeTabState[0];
  var setActiveTab = activeTabState[1];

  var visibleCountState = useState(PAGE_SIZE);
  var visibleCount = visibleCountState[0];
  var setVisibleCount = visibleCountState[1];

  // Reset page size on tab change
  useEffect(function() {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  var unreadNotifications = notifications.filter(function(n) { return !n.read; });
  var readNotifications = notifications.filter(function(n) { return n.read; });

  var getFilteredNotifications = function() {
    switch (activeTab) {
      case 'unread':
        return unreadNotifications;
      case 'read':
        return readNotifications;
      default:
        return notifications;
    }
  };

  var filteredNotifications = getFilteredNotifications();
  var paginatedNotifications = filteredNotifications.slice(0, visibleCount);
  var hasMore = filteredNotifications.length > visibleCount;

  var handleLoadMore = function() {
    setVisibleCount(function(prev) { return prev + PAGE_SIZE; });
  };

  var getIconConfig = function(type) {
    return ICON_CONFIG[type] || ICON_CONFIG.info;
  };

  var handleNotificationClick = function(item) {
    if (!item.read) {
      markAsRead(item.id);
    }
    // Navigate if notification has a route
    if (item.route) {
      onClose();
      history.push(item.route);
    } else if (item.link) {
      onClose();
      history.push(item.link);
    }
  };

  var handleMarkRead = function(e, id) {
    e.stopPropagation();
    markAsRead(id);
  };

  var handleDelete = function(e, id) {
    e.stopPropagation();
    deleteNotification(id);
  };

  /* Miniatura e botão levam ao mesmo lugar: quem clica na capa espera abrir o
     vídeo, e o vídeo mora na tela de versões. */
  var abrirAcao = function(item) {
    if (!item.read) markAsRead(item.id);
    history.push(item.action_url);
    if (typeof onClose === 'function') onClose();
  };

  var renderNotification = function(item) {
    var cfg = getIconConfig(item.type);
    var isUnread = !item.read;

    return React.createElement(NotificationRow, {
      key: item.id,
      isUnread: isUnread,
      notifType: item.type || 'info',
      onClick: function() { handleNotificationClick(item); }
    },
      React.createElement(NotificationIconCircle, {
        bg: cfg.bg,
        borderColor: cfg.border
      },
        React.createElement(Icon, {
          type: cfg.icon,
          theme: 'filled',
          style: { fontSize: 18, color: cfg.color }
        })
      ),
      React.createElement(NotificationBody, null,
        item.title
          ? React.createElement('div', {
              style: { fontWeight: 600, fontSize: 13.5, color: '#1f1f1f', marginBottom: 3 },
            }, item.title)
          : null,
        React.createElement(NotificationMessage, {
          dangerouslySetInnerHTML: { __html: item.message }
        }),
        item.image_url && item.action_url
          ? React.createElement(VideoThumb, {
              onClick: function(e) { e.stopPropagation(); abrirAcao(item); }
            },
              React.createElement('img', {
                src: item.image_url,
                alt: '',
                /* Capa que não carrega deixaria um retângulo vazio no meio do
                   aviso; melhor sumir e manter só o texto e o botão. */
                onError: function(e) { e.target.parentNode.style.display = 'none'; }
              }),
              React.createElement('span', { className: 'play' },
                React.createElement(Icon, { type: 'caret-right', theme: 'filled' })
              )
            )
          : null,
        /* O botão leva direto à tela. Sem ele, agir a partir do aviso exige
           ler o caminho no texto, decorar e navegar por conta própria. */
        item.action_url
          ? React.createElement('button', {
              onClick: function(e) { e.stopPropagation(); abrirAcao(item); },
              style: {
                marginTop: 8, border: '1px solid ' + cfg.border, cursor: 'pointer',
                background: cfg.bg, color: cfg.color, fontFamily: 'inherit',
                fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6,
              },
            }, item.action_label || 'Abrir')
          : null,
        React.createElement(NotificationMeta, null,
          React.createElement(NotificationTime, null,
            moment(item.created_at).fromNow()
          ),
          isUnread && React.createElement(UnreadDot, null)
        )
      ),
      React.createElement(RowActions, null,
        isUnread && React.createElement(Tooltip, { title: 'Marcar como lida' },
          React.createElement(ActionBtn, {
            type: 'link',
            size: 'small',
            onClick: function(e) { handleMarkRead(e, item.id); }
          },
            React.createElement(Icon, { type: 'eye' })
          )
        ),
        React.createElement(Tooltip, { title: 'Excluir' },
          React.createElement(ActionBtn, {
            type: 'link',
            size: 'small',
            onClick: function(e) { handleDelete(e, item.id); }
          },
            React.createElement(Icon, { type: 'delete' })
          )
        )
      )
    );
  };

  var renderGroupedList = function() {
    var groups = groupByTime(paginatedNotifications);
    var elements = [];

    Object.keys(groups).forEach(function(groupName) {
      var items = groups[groupName];
      if (items.length === 0) return;

      elements.push(
        React.createElement(GroupHeader, { key: 'group-' + groupName }, groupName)
      );
      items.forEach(function(item) {
        elements.push(renderNotification(item));
      });
    });

    return elements;
  };

  var renderEmptyState = function() {
    var emptyMessages = {
      all: { title: 'Tudo tranquilo!', desc: 'Nenhuma notificação por enquanto. Quando algo acontecer, você será avisado aqui.', icon: 'bell' },
      unread: { title: 'Tudo em dia!', desc: 'Você já leu todas as notificações. Continue assim!', icon: 'check-circle' },
      read: { title: 'Nada por aqui', desc: 'As notificações lidas aparecerão aqui.', icon: 'inbox' },
    };
    var msg = emptyMessages[activeTab] || emptyMessages.all;

    return React.createElement(EmptyContainer, null,
      React.createElement(EmptyIcon, null,
        React.createElement(Icon, { type: msg.icon, style: { fontSize: 32, color: '#bfbfbf' } })
      ),
      React.createElement(EmptyTitle, null, msg.title),
      React.createElement(EmptyDesc, null, msg.desc)
    );
  };

  var unreadCount = unreadNotifications.length;

  var drawerTitle = React.createElement(DrawerHeader, null,
    React.createElement(DrawerTitle, null,
      'Notificações',
      unreadCount > 0 && React.createElement('span', {
        style: { fontSize: 13, fontWeight: 400, color: '#8c8c8c' }
      }, '(' + unreadCount + (unreadCount === 1 ? ' nova' : ' novas') + ')'),
      realtimeConnected && React.createElement(Tooltip, { title: 'Conectado em tempo real' },
        React.createElement(LiveDot, null)
      )
    ),
    unreadCount > 0 && React.createElement(Button, {
      size: 'small',
      icon: 'check',
      onClick: markAllAsRead,
      style: { borderRadius: 6 }
    }, 'Ler todas')
  );

  return React.createElement(Drawer, {
    title: drawerTitle,
    placement: 'right',
    width: 480,
    onClose: onClose,
    visible: visible,
    bodyStyle: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }
  },
    React.createElement(StyledTabs, {
      activeKey: activeTab,
      onChange: setActiveTab,
      style: { padding: '0 16px' }
    },
      React.createElement(TabPane, {
        tab: React.createElement(TabLabel, null,
          'Todas',
          notifications.length > 0 && React.createElement(Badge, {
            count: notifications.length,
            style: { backgroundColor: '#d9d9d9', color: '#666' },
            overflowCount: 99
          })
        ),
        key: 'all'
      }),
      React.createElement(TabPane, {
        tab: React.createElement(TabLabel, null,
          'Não lidas',
          unreadCount > 0 && React.createElement(Badge, {
            count: unreadCount,
            overflowCount: 99
          })
        ),
        key: 'unread'
      }),
      React.createElement(TabPane, {
        tab: React.createElement(TabLabel, null,
          'Lidas',
          readNotifications.length > 0 && React.createElement(Badge, {
            count: readNotifications.length,
            style: { backgroundColor: '#d9d9d9', color: '#666' },
            overflowCount: 99
          })
        ),
        key: 'read'
      })
    ),
    React.createElement('div', { style: { flex: 1, overflowY: 'auto' } },
      filteredNotifications.length === 0
        ? renderEmptyState()
        : React.createElement('div', null,
            renderGroupedList(),
            hasMore && React.createElement(LoadMoreBtn, { onClick: handleLoadMore },
              React.createElement(Icon, { type: 'down', style: { marginRight: 6 } }),
              'Carregar mais (' + (filteredNotifications.length - visibleCount) + ' restantes)'
            )
          )
    )
  );
};

export default NotificationDrawer;
